/**
 * Mijn Notes — verkenner (Joris, 1 sep 2026).
 *
 * Links de boom: Losse notities · Full Frame · Accounts → (Algemeen + eigen
 * mappen + openstaande Gripp-projecten). Alleen accounts met actieve projecten
 * zijn zichtbaar; mappen zijn vrij aan te maken (ook voor werk buiten Gripp).
 * Rechts de editor (slaat op in de huidige selectie) en de notities daarvan.
 * Zelfde stijl als Mijn To-Do's; die volgt later deze structuur.
 */
import { useNavigate } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, InputAdornment, List, ListItemButton, ListItemText, Menu, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import MicNoneIcon from '@mui/icons-material/MicNone';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { useMsal } from '@azure/msal-react';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import ReactMarkdown from 'react-markdown';
import { MARKDOWN_COMPONENTS } from '../../ui-components/MarkdownAfbeelding';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import PageHeader from '../../ui-components/PageHeader';
import NotitieEditor from '../../ui-components/NotitieEditor';
import MailNotitieTekst, { isMailNotitie } from '../../ui-components/MailNotitieTekst';

const FONT = '"Inter", system-ui, sans-serif';

// Zelfde markdown-weergave als de projectnotitie-kaarten.
export function NotitieTekst({ tekst, sx, onderwerp }) {
  // Mail-notities: ruis weg, handtekening en eerdere berichten ingeklapt (16 sep).
  if (isMailNotitie(tekst)) return <MailNotitieTekst tekst={tekst} sx={sx} onderwerp={onderwerp} />;
  return (
    <Typography component="div" variant="body2"
      sx={{ lineHeight: 1.6, '& p': { m: 0, mb: 0.5 }, '& ul': { m: 0, pl: 2.5 }, ...sx }}>
      <ReactMarkdown components={MARKDOWN_COMPONENTS}>{String(tekst || '')}</ReactMarkdown>
    </Typography>
  );
}

// Zoekresultaten: lange notities eerst begrensd tonen (anders scrol je door één mail
// van drie schermen) — "Meer" klapt uit.
function IngeklapteNotitie({ tekst, inklappen, onderwerp }) {
  const [open, setOpen] = useState(false);
  const lang = String(tekst || '').length > 700;
  if (!inklappen || !lang || open) {
    return (
      <>
        <NotitieTekst tekst={tekst} onderwerp={onderwerp} />
        {inklappen && lang && open && (
          <Button size="small" onClick={() => setOpen(false)} sx={{ textTransform: 'none', p: 0, minWidth: 0, fontSize: 12, color: '#64748b', mt: 0.5, mr: 1.5 }}>Minder</Button>
        )}
      </>
    );
  }
  return (
    <Box>
      <Box sx={{ maxHeight: 220, overflow: 'hidden', position: 'relative',
        '&::after': { content: '""', position: 'absolute', left: 0, right: 0, bottom: 0, height: 48, background: 'linear-gradient(rgba(254,252,232,0), #fefce8)' } }}>
        <NotitieTekst tekst={tekst} onderwerp={onderwerp} />
      </Box>
      <Button size="small" onClick={() => setOpen(true)} sx={{ textTransform: 'none', p: 0, minWidth: 0, fontSize: 12, fontWeight: 700, mt: 0.25 }}>Meer</Button>
    </Box>
  );
}

// ── Sjablonen (uit het OneNote-template "ALGEMEEN", maart 2026) ──
const datumNL = () => new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
const regels = (velden) => velden.map(([k, v]) => `**${k}** ${v || ''}`).join('  \n');
const SJABLONEN = [
  { key: 'basis', label: 'Intake klant – film basis', maak: ({ klant, pl }) => `## Intake klant – film basis\n${regels([
    ['Klantnaam:', klant], ['Contactpersoon:', ''], ['FF projectleider:', pl], ['Doel:', ''], ['Doelgroep:', ''],
    ['Deliverables / kanalen:', ''], ['Beoogde lengte:', ''], ['Datum draaidag:', ''], ['Gewenste deadline:', ''],
    ['Script by FF?', ''], ['Regie by FF?', ''], ['Budgetindicatie:', ''],
  ])}` },
  { key: 'uitgebreid', label: 'Intake klant – uitgebreid', maak: () => `## Intake klant – uitgebreid\n${regels([
    ['Aanleiding van content?', ''], ['Hoofdboodschap:', ''], ['Welke emotie?', ''], ['Tone of voice:', ''], ['Call to action:', ''],
    ['Randvoorwaarden oplevering:', 'ondertiteling? · formaten? · social versies?'], ['Quitclaim nodig?', ''], ['Locaties by FF?', ''], ['Cast by FF?', ''],
  ])}` },
  { key: 'aantekeningen', label: 'Aantekeningen gesprek (aanwezig, datum)', maak: ({ pl }) => `## Aantekeningen – ${pl || ''}\n${regels([
    ['Aanwezig:', ''], ['Datum:', datumNL()],
  ])}\n\n[Inspiratievragen (wiki)](https://wiki.fullframe.nl/doc/briefing-gesprekken-j2g3dpkXcQ)\n\n**Notes:**\n\n**Doel en doelgroep:**\n\n**Stijl:**\n\n**Deliverables:**\n\n**Planning:**\n\n**Aanpak:**\n` },
  { key: 'alles', label: 'Volledig template (basis + uitgebreid + aantekeningen)', maak: (ctx) => [SJABLONEN[0].maak(ctx), SJABLONEN[1].maak(ctx), SJABLONEN[2].maak(ctx)].join('\n\n') },
];
const meetingBlok = (m) => {
  const datum = m.datum ? new Date(m.datum).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  return `### 🎙️ Meeting: ${m.titel}${datum ? ` — ${datum}` : ''}\n${m.samenvatting || '_Nog geen samenvatting._'}\n\n[Open meeting](/MeetingDashboard/${m.id})`;
};

/** Kiezer voor een FF Recorder-meeting: zoeken op titel, gekoppelde meetings van
 *  het gekozen project bovenaan. Module-niveau (geen remount bij elke toets). */
function MeetingKoppelDialog({ open, onClose, projectId, onKies }) {
  const [q, setQ] = useState('');
  const [lijst, setLijst] = useState(null);
  useEffect(() => {
    if (!open) return undefined;
    let dood = false;
    setLijst(null);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: q.trim() });
        if (projectId) params.set('projectId', projectId);
        const r = await authFetch(`${BACKEND_URL}/Api/project-notes/meetings?${params}`);
        const d = await leesJson(r, 'Meetings zoeken');
        if (!dood) setLijst(d.meetings || []);
      } catch (e) { if (!dood) { meldFout(e, 'Meetings zoeken'); setLijst([]); } }
    }, 250);
    return () => { dood = true; clearTimeout(t); };
  }, [open, q, projectId]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 16 }}>🎙️ Meeting koppelen</DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth size="small" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Zoek op titel…" sx={{ mb: 1.5 }} />
        {lijst === null ? <CircularProgress size={20} /> : lijst.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: '#94a3b8', py: 2, textAlign: 'center' }}>Geen meetings gevonden (alleen meetings waar je bij was of host van bent).</Typography>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 420, overflowY: 'auto' }}>
            {lijst.map((m) => (
              <ListItemButton key={m.id} onClick={() => onKies(m)} sx={{ borderRadius: 1, alignItems: 'flex-start' }}>
                <ListItemText
                  primary={(
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{m.titel}</Typography>
                      {m.gekoppeld && <Chip size="small" label="bij dit project" sx={{ height: 18, fontSize: 10.5, bgcolor: '#dcfce7', color: '#166534' }} />}
                    </Stack>
                  )}
                  secondary={`${m.datum ? new Date(m.datum).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}${m.duur ? ` · ${m.duur} min` : ''}${m.samenvatting ? ` · ${m.samenvatting.slice(0, 110)}…` : ' · nog geen samenvatting'}`}
                  secondaryTypographyProps={{ sx: { fontSize: 12 } }} />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}

const VASTE_ITEMS = [
  { type: 'los', label: '⚡ Snelle notities', hint: 'nog nergens bij — later in te delen' },
  { type: 'fullframe', label: '🏠 Full Frame', hint: 'intern, niet klantgebonden' },
];

function BoomRij({ actief, diepte = 0, onClick, children, extra }) {
  return (
    <Box onClick={onClick} sx={{
      display: 'flex', alignItems: 'center', gap: 0.5, pl: 1 + diepte * 1.5, pr: 0.5, py: 0.4,
      cursor: 'pointer', borderRadius: 1, bgcolor: actief ? '#eef2ff' : 'transparent',
      '&:hover': { bgcolor: actief ? '#eef2ff' : '#f8fafc' },
      '&:hover .rij-extra': { opacity: 1 },
    }}>
      {children}
      {extra}
    </Box>
  );
}

export default function Notities() {
  const [boom, setBoom] = useState(null);
  const [openAccounts, setOpenAccounts] = useState(new Set());
  const [selectie, setSelectie] = useState({ type: 'los', label: '⚡ Snelle notities' });
  const navigate = useNavigate();
  const [tekst, setTekst] = useState('');
  const [busy, setBusy] = useState(false);
  // Bewerken + tussentijds opslaan (Joris, 15 sep): een potlood zet een bestaande notitie in de
  // editor; tijdens het typen wordt na 4 s rust automatisch bewaard (eerst als nieuwe notitie,
  // daarna als wijziging van diezelfde notitie). 'Opslaan' rondt af en maakt de editor leeg.
  const [bewerkId, setBewerkId] = useState(null);
  const bewerkIdRef = useRef(null);
  useEffect(() => { bewerkIdRef.current = bewerkId; }, [bewerkId]);
  const [autosaveStatus, setAutosaveStatus] = useState('');
  const laatstBewaardRef = useRef('');
  const autosaveBezigRef = useRef(false);
  const generatieRef = useRef(0);   // loopt op bij Opslaan/annuleren; late autosave-antwoorden worden genegeerd
  const editorRef = useRef(null);
  const { accounts } = useMsal();
  const mijnNaam = accounts[0]?.name || '';
  const [notes, setNotes] = useState(null);
  const [alleenMijn, setAlleenMijn] = useState(() => { try { return localStorage.getItem('notes:alleenMijn') === '1'; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem('notes:alleenMijn', alleenMijn ? '1' : '0'); } catch { /* privémodus */ } }, [alleenMijn]);
  const [sjabloonAnchor, setSjabloonAnchor] = useState(null);
  const [meetingOpen, setMeetingOpen] = useState(false);
  // Zoekbalk: één veld voor alles — filtert de boom én doorzoekt alle notities.
  const [zoek, setZoek] = useState('');
  const [treffers, setTreffers] = useState(null); // undefined = bezig
  const zoekRef = useRef(null);
  const zoekActief = zoek.trim().length >= 2;
  useEffect(() => {
    if (!zoekActief) { setTreffers(null); return undefined; }
    let dood = false;
    setTreffers(undefined);
    const t = setTimeout(async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/project-notes/zoek?q=${encodeURIComponent(zoek.trim())}${alleenMijn ? `&mine=1&mine_name=${encodeURIComponent(mijnNaam)}` : ''}`);
        const d = await leesJson(r, 'Notities zoeken');
        if (!dood) setTreffers(d.notes || []);
      } catch (e) { if (!dood) { meldFout(e, 'Notities zoeken'); setTreffers([]); } }
    }, 250);
    return () => { dood = true; clearTimeout(t); };
  }, [zoek, zoekActief, alleenMijn, mijnNaam]);
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === 'k') { e.preventDefault(); zoekRef.current?.focus(); zoekRef.current?.select(); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const laadBoom = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-notes/boom`);
      const d = await leesJson(r, 'Notitieboom laden');
      setBoom(d.accounts || []);
    } catch (e) { meldFout(e, 'Notitieboom laden'); setBoom([]); }
  }, []);
  useEffect(() => { laadBoom(); }, [laadBoom]);
  useEffect(() => registreerOngedaanSneltoets(), []);

  const laadNotes = useCallback(async (sel) => {
    setNotes(null);
    try {
      const params = new URLSearchParams({ scope: sel.type });
      if (sel.account) params.set('account', sel.account);
      if (sel.folder) params.set('folder', sel.folder);
      if (sel.projectId) params.set('projectId', sel.projectId);
      if (alleenMijn) { params.set('mine', '1'); params.set('mine_name', mijnNaam); }
      const r = await authFetch(`${BACKEND_URL}/Api/project-notes/overzicht?${params}`);
      const d = await leesJson(r, 'Notities laden');
      setNotes(d.notes || []);
    } catch (e) { meldFout(e, 'Notities laden'); setNotes([]); }
  }, [alleenMijn, mijnNaam]);
  useEffect(() => { laadNotes(selectie); }, [selectie, laadNotes]);

  // Een klik in de boom is een expliciete keuze: sluit de zoekopdracht, anders bleven alle
  // zoektreffers staan terwijl je op één project klikte (Joris, 6 sep).
  // Andere plek gekozen: de editor blijft staan als concept, maar hoort niet meer bij de vorige
  // notitie (anders overschrijft de volgende autosave notitie A vanuit project B — Codex #11).
  const kies = (sel) => { if (window.scrollY > 120) window.scrollTo({ top: 0, behavior: 'smooth' }); setSelectie(sel); setZoek(''); generatieRef.current += 1; setBewerkId(null); bewerkIdRef.current = null; laatstBewaardRef.current = ''; setAutosaveStatus(''); };

  // Boom gefilterd op de zoekterm (account, map of project); alles open tijdens zoeken.
  const boomZichtbaar = useMemo(() => {
    if (!boom || !zoekActief) return boom;
    const q = zoek.trim().toLowerCase();
    return boom.map((acc) => {
      const accMatch = acc.account.toLowerCase().includes(q);
      const projecten = accMatch ? acc.projecten : acc.projecten.filter((p) => `${p.nummer} ${p.naam}`.toLowerCase().includes(q));
      const mappen = accMatch ? (acc.mappen || []) : (acc.mappen || []).filter((m) => m.toLowerCase().includes(q));
      return (accMatch || projecten.length || mappen.length) ? { ...acc, projecten, mappen } : null;
    }).filter(Boolean);
  }, [boom, zoek, zoekActief]);

  // Alleen accounts mét notities of mappen voorop; de rest (de hele klantenlijst) staat
  // ingeklapt onder "Overige accounts" met een eigen filter (Joris 17 sep: "vervuild").
  const [overigOpen, setOverigOpen] = useState(false);
  const [alleProjectenVan, setAlleProjectenVan] = useState(() => new Set()); // accounts waar ook projecten zonder notities open staan
  const [overigFilter, setOverigFilter] = useState('');
  const { boomGebruikt, boomOverig } = useMemo(() => {
    const lijst = boomZichtbaar || [];
    if (zoekActief) return { boomGebruikt: lijst, boomOverig: [] };
    const gebruikt = [], overig = [];
    lijst.forEach((acc) => {
      const inGebruik = (acc.notities || 0) > 0 || (acc.mappen || []).length > 0 || openAccounts.has(acc.account) || selectie.account === acc.account;
      (inGebruik ? gebruikt : overig).push(acc);
    });
    const f = overigFilter.trim().toLowerCase();
    return { boomGebruikt: gebruikt, boomOverig: f ? overig.filter((a) => a.account.toLowerCase().includes(f)) : overig };
  }, [boomZichtbaar, zoekActief, openAccounts, selectie.account, overigFilter]);

  // Vanuit een zoektreffer naar de plek in de boom (project → account/map → los).
  const openVanuitZoek = (n) => {
    setZoek('');
    if (n.projectId) {
      const acc = (boom || []).find((a) => a.projecten.some((p) => String(n.projectId).includes(p.id)));
      const p = acc?.projecten.find((x) => String(n.projectId).includes(x.id));
      if (acc && p) {
        setOpenAccounts(new Set([acc.account]));
        kies({ type: 'project', projectId: p.id, account: acc.account, label: `${p.nummer}. ${p.naam}` });
        return;
      }
    }
    if (n.klant === 'Full Frame') { kies({ type: 'fullframe', label: '🏠 Full Frame' }); return; }
    if (n.klant) {
      const acc = (boom || []).find((a) => a.account === n.klant || (a.bedrijven || []).includes(n.klant));
      const account = acc?.account || n.klant;
      setOpenAccounts(new Set([account]));
      kies({ type: 'account', account, folder: n.folder || undefined, label: `${account} · ${n.folder || 'Algemeen'}` });
      return;
    }
    kies({ type: 'los', label: '⚡ Snelle notities' });
  };

  const titelUit = (t) => t.trim().split('\n')[0].replace(/^[#-]\s*/, '').slice(0, 50) || 'Notitie';
  const plekExtra = () => {
    const extra = {};
    if (selectie.type === 'fullframe') extra.klantNaam = 'Full Frame';
    if (selectie.type === 'account') { extra.klantNaam = selectie.account; if (selectie.folder) extra.folder = selectie.folder; }
    if (selectie.type === 'project') extra.projectId = selectie.projectId;
    return extra;
  };
  /** Schrijft de editor weg: PATCH als er al een notitie aan hangt, anders POST (en onthoud het id). */
  const schrijfWeg = async (inhoud) => {
    const generatie = generatieRef.current;
    const t = inhoud.trim();
    const huidigId = bewerkIdRef.current;   // ref, niet de closure: een net afgeronde autosave telt mee (Codex #10)
    if (huidigId) {
      const r = await authFetch(`${BACKEND_URL}/Api/project-notes/${huidigId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: titelUit(t), notes: t }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Opslaan mislukt');
      return huidigId;
    }
    const r = await authFetch(`${BACKEND_URL}/Api/project-notes`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: titelUit(t), notes: t, source: 'Manual', ...plekExtra() }),
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Opslaan mislukt');
    const d = await r.json().catch(() => ({}));
    const id = d?.record?.id || d?.id || null;
    if (id && generatie === generatieRef.current) { setBewerkId(id); bewerkIdRef.current = id; }
    return id;
  };
  const bewerk = (n) => {
    // Andere notitie in de editor: late autosave-antwoorden en een al geplande autosave van
    // de vorige horen daar niet meer bij (anders overschrijft die notitie B of omgekeerd —
    // Codex 30 okt). Ref meteen zetten, niet pas na de render.
    generatieRef.current += 1;
    bewerkIdRef.current = n.id;
    setBewerkId(n.id);
    setTekst(n.notes || '');
    laatstBewaardRef.current = (n.notes || '').trim();
    setAutosaveStatus('');
    try { editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch { /* geen scroll */ }
  };
  const annuleerBewerken = () => { generatieRef.current += 1; setBewerkId(null); bewerkIdRef.current = null; setTekst(''); laatstBewaardRef.current = ''; setAutosaveStatus(''); };

  // Tussentijds opslaan: 4 s na de laatste toetsaanslag, vanaf 25 tekens.
  useEffect(() => {
    const t = tekst.trim();
    if (t.length < 25 || t === laatstBewaardRef.current || busy) return undefined;
    const generatie = generatieRef.current;
    const timer = setTimeout(async () => {
      // Intussen een andere notitie/plek gekozen of afgerond: deze tekst hoort daar niet bij.
      if (autosaveBezigRef.current || generatie !== generatieRef.current) return;
      autosaveBezigRef.current = true;
      try {
        await schrijfWeg(t);
        if (generatie !== generatieRef.current) return;   // laat antwoord: niet meer de open notitie
        laatstBewaardRef.current = t;
        setAutosaveStatus(`Automatisch bewaard · ${new Date().toTimeString().slice(0, 5)}`);
        laadNotes(selectie);
      } catch { if (generatie === generatieRef.current) setAutosaveStatus('Automatisch bewaren mislukt — gebruik Opslaan'); }
      finally { autosaveBezigRef.current = false; }
    }, 4000);
    return () => clearTimeout(timer);
    // selectie: na een plekwissel (kies) wordt een concept opnieuw ingepland — op de nieuwe plek.
  }, [tekst, bewerkId, busy, selectie]); // eslint-disable-line react-hooks/exhaustive-deps

  const bewaar = async () => {
    if (!tekst.trim() || busy) return;
    setBusy(true);
    try {
      // Loopt er nog een autosave? Even wachten, anders schrijven twee POSTs dezelfde notitie (Codex #10).
      for (let i = 0; i < 150 && autosaveBezigRef.current; i += 1) await new Promise((r) => setTimeout(r, 100)); // eslint-disable-line no-await-in-loop
      const t = tekst.trim();
      if (t !== laatstBewaardRef.current || !bewerkIdRef.current) await schrijfWeg(t);
      generatieRef.current += 1;
      setTekst('');
      setBewerkId(null);
      laatstBewaardRef.current = '';
      setAutosaveStatus('');
      toast.success('Notitie opgeslagen');
      laadNotes(selectie);
      laadBoom(); // tellers in de boom bijwerken
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const verwijder = async (note) => {
    if (!(await bevestig({ titel: 'Notitie verwijderen?', tekst: `"${note.onderwerp || 'Notitie'}" wordt verwijderd. Dit kan niet ongedaan worden gemaakt.`, knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-notes/${note.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Verwijderen mislukt');
      toast.success('Notitie verwijderd');
      setNotes((prev) => (prev || []).filter((n) => n.id !== note.id));
      laadBoom(); // tellers in de boom bijwerken
      setTreffers((prev) => (Array.isArray(prev) ? prev.filter((n) => n.id !== note.id) : prev));
    } catch (e) { toast.error(e.message); }
  };

  const voegToe = (blok) => setTekst((t) => (t.trim() ? `${t.replace(/\s+$/, '')}\n\n${blok}` : blok));
  const kiesSjabloon = (sj) => {
    setSjabloonAnchor(null);
    voegToe(sj.maak({ klant: selectie.type === 'account' || selectie.type === 'project' ? (selectie.account || '') : '', pl: mijnNaam }));
  };
  const kiesMeeting = async (m) => {
    setMeetingOpen(false);
    voegToe(meetingBlok(m));
    if (selectie.type === 'project' && selectie.projectId && !m.gekoppeld) {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/project-notes/meetings/koppel`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ projectId: selectie.projectId, meetingId: m.id }),
        });
        if (r.ok) toast.success('Meeting ook aan het project gekoppeld');
      } catch { /* koppeling is een extraatje; de tekst staat er */ }
    }
  };

  const verplaats = async (note, extra) => {
    // Vorige plek in dezelfde vorm als VerplaatsNaar levert, zodat "Ongedaan maken" hem terugzet.
    const terug = note.projectId ? { projectId: note.projectId } : { klantNaam: note.klant || null, folder: note.folder || null };
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-notes/${note.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...extra, verplaats: true }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Verplaatsen mislukt');
      biedOngedaanAan('Verplaatst', async () => {
        const t = await authFetch(`${BACKEND_URL}/Api/project-notes/${note.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...terug, verplaats: true }),
        });
        if (!t.ok) throw new Error('Terugzetten mislukt');
        laadNotes(selectie);
        laadBoom();
      });
      laadNotes(selectie);
    } catch (e) { toast.error(e.message); }
  };

  const nieuweMap = async (account) => {
    const naam = window.prompt(`Nieuwe map onder ${account} (bv. een project dat nog niet in Gripp staat):`);
    if (!naam || !naam.trim()) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-notes/mappen`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account, map: naam.trim() }),
      });
      if (!r.ok) throw new Error();
      laadBoom();
      setOpenAccounts(new Set([account]));
    } catch { toast.error('Map aanmaken mislukt'); }
  };

  const verwijderMap = async (account, naam) => {
    if (!(await bevestig({ titel: `Map "${naam}" verwijderen?`, tekst: 'Notities erin verhuizen naar Algemeen.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      await authFetch(`${BACKEND_URL}/Api/project-notes/mappen`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account, map: naam, actie: 'remove' }),
      });
      laadBoom();
      if (selectie.folder === naam) kies({ type: 'account', account, label: `${account} · Algemeen` });
    } catch { toast.error('Verwijderen mislukt'); }
  };

  // Accordeon: één account tegelijk open (Joris, 17 sep) — de rest klapt dicht.
  const toggleAccount = (account) => setOpenAccounts((prev) => (prev.has(account) ? new Set() : new Set([account])));

  const isActief = (sel) => selectie.type === sel.type
    && (selectie.account || '') === (sel.account || '')
    && (selectie.folder || '') === (sel.folder || '')
    && (selectie.projectId || '') === (sel.projectId || '');

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 3 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader icon="sticky_note_2" title="Notities"
        subtitle="Snel een notitie — per account, project of map, net als je to-do's"
        rightActions={<Button size="small" onClick={() => navigate('/notes/onenote-import')} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>📓 OneNote importeren</Button>} />

      <TextField inputRef={zoekRef} value={zoek} onChange={(e) => setZoek(e.target.value)} fullWidth
        placeholder="Zoek in alle notities, accounts en projecten…   ⌘K"
        onKeyDown={(e) => { if (e.key === 'Escape') setZoek(''); }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8', fontSize: 24 }} /></InputAdornment>,
          endAdornment: zoek ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setZoek('')} aria-label="Zoekopdracht wissen"><CloseIcon fontSize="small" /></IconButton>
            </InputAdornment>
          ) : null,
          sx: { fontFamily: FONT, fontSize: 16.5, height: 56, borderRadius: 2.5, bgcolor: '#fff' },
        }}
        sx={{ mb: 2.5,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#c7d2fe' },
          '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1', borderWidth: 2 } }} />

      <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
        {/* ── Boom ── */}
        {/* Eigen scrollkader: een lange boom (Enexis met 40 projecten) trok de hele pagina naar
            beneden, waardoor de notities rechts uit beeld raakten (Joris, 17 sep). */}
        <Box sx={{ width: 270, flexShrink: 0, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff', p: 1, position: 'sticky', top: 16, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', alignSelf: 'flex-start' }}>
          {VASTE_ITEMS.map((v) => (
            <BoomRij key={v.type} actief={selectie.type === v.type}
              onClick={() => kies({ type: v.type, label: v.label, hint: v.hint })}>
              <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>{v.label}</Typography>
            </BoomRij>
          ))}
          <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4, mt: 1.5, mb: 0.5, pl: 1 }}>
            Accounts
          </Typography>
          {boomZichtbaar === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={16} /></Box>
          ) : (() => { const renderProject = (acc, p) => (
            <BoomRij key={p.id} diepte={1} actief={isActief({ type: 'project', projectId: p.id })}
              onClick={() => kies({ type: 'project', projectId: p.id, account: acc.account, label: `${p.nummer}. ${p.naam}` })}>
              <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12.5, flex: 1, minWidth: 0,
                color: p.aantal ? '#334155' : '#a0aec0', fontWeight: p.aantal ? 600 : 400 }}>
                🎬 {p.nummer ? `${p.nummer}. ` : ''}{p.naam}
              </Typography>
              {p.aantal > 0 && <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: '#6366f1', bgcolor: '#eef2ff', px: 0.7, borderRadius: 1 }}>{p.aantal}</Typography>}
            </BoomRij>
          );
          const renderAccount = (acc) => {
            const open = zoekActief || openAccounts.has(acc.account);
            return (
              <Box key={acc.account}>
                <BoomRij onClick={() => toggleAccount(acc.account)}>
                  {open ? <ExpandMoreIcon sx={{ fontSize: 15, color: '#94a3b8' }} /> : <ChevronRightIcon sx={{ fontSize: 15, color: '#94a3b8' }} />}
                  <Typography noWrap sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: '#334155', flex: 1 }}>{acc.account}</Typography>
                  {acc.notities > 0 && <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: '#94a3b8' }}>{acc.notities}</Typography>}
                </BoomRij>
                {open && (
                  <>
                    <BoomRij diepte={1} actief={isActief({ type: 'account', account: acc.account })}
                      onClick={() => kies({ type: 'account', account: acc.account, label: `${acc.account} · Algemeen` })}>
                      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: acc.mapAantal?.Algemeen ? '#475569' : '#94a3b8', flex: 1 }}>📂 Algemeen</Typography>
                      {acc.mapAantal?.Algemeen > 0 && <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#94a3b8' }}>{acc.mapAantal.Algemeen}</Typography>}
                    </BoomRij>
                    {(acc.mappen || []).map((m) => (
                      <BoomRij key={m} diepte={1} actief={isActief({ type: 'account', account: acc.account, folder: m })}
                        onClick={() => kies({ type: 'account', account: acc.account, folder: m, label: `${acc.account} · ${m}` })}
                        extra={(
                          <IconButton className="rij-extra" size="small" sx={{ opacity: 0, p: 0.25 }}
                            onClick={(e) => { e.stopPropagation(); verwijderMap(acc.account, m); }}>
                            <DeleteOutlineIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                          </IconButton>
                        )}>
                        <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12.5, color: acc.mapAantal?.[m] ? '#475569' : '#94a3b8', flex: 1 }}>📁 {m}</Typography>
                        {acc.mapAantal?.[m] > 0 && <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#94a3b8', mr: 0.5 }}>{acc.mapAantal[m]}</Typography>}
                      </BoomRij>
                    ))}
                    {(() => {
                      const alles = zoekActief || alleProjectenVan.has(acc.account);
                      const metNotities = acc.projecten.filter((p) => p.aantal > 0 || isActief({ type: 'project', projectId: p.id }));
                      const zichtbaar = alles ? acc.projecten : metNotities;
                      const verborgen = acc.projecten.length - zichtbaar.length;
                      return (
                        <>
                          {zichtbaar.map((p) => renderProject(acc, p))}
                          {verborgen > 0 && (
                            <BoomRij diepte={1} onClick={() => setAlleProjectenVan((prev) => new Set(prev).add(acc.account))}>
                              <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8' }}>… {verborgen} project{verborgen === 1 ? '' : 'en'} zonder notities</Typography>
                            </BoomRij>
                          )}
                        </>
                      );
                    })()}
                    <BoomRij diepte={1} onClick={() => nieuweMap(acc.account)}>
                      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8' }}>＋ map</Typography>
                    </BoomRij>
                  </>
                )}
              </Box>
            );
          };
          return (
            <>
              {boomGebruikt.map(renderAccount)}
              {boomGebruikt.length === 0 && !zoekActief && (
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8', pl: 1, py: 0.5 }}>Nog geen account met notities.</Typography>
              )}
              {!zoekActief && (boomOverig.length > 0 || overigFilter) && (
                <Box sx={{ mt: 1 }}>
                  <BoomRij onClick={() => setOverigOpen((v) => !v)}>
                    {overigOpen ? <ExpandMoreIcon sx={{ fontSize: 15, color: '#94a3b8' }} /> : <ChevronRightIcon sx={{ fontSize: 15, color: '#94a3b8' }} />}
                    <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: '#94a3b8', flex: 1 }}>Overige accounts</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: '#cbd5e1' }}>{boomOverig.length}</Typography>
                  </BoomRij>
                  {overigOpen && (
                    <Box sx={{ pl: 1 }}>
                      <TextField size="small" fullWidth placeholder="Filter accounts…" value={overigFilter} onChange={(e) => setOverigFilter(e.target.value)}
                        sx={{ my: 0.5, '& .MuiInputBase-input': { fontSize: 12.5, py: 0.6 } }} />
                      {boomOverig.slice(0, 60).map(renderAccount)}
                      {boomOverig.length > 60 && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8', pl: 1 }}>… nog {boomOverig.length - 60}, typ om te filteren</Typography>}
                    </Box>
                  )}
                </Box>
              )}
            </>
          ); })()}
        </Box>

        {/* ── Editor + notities van de selectie ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!zoekActief && <Box sx={{ mb: 2.5, p: 2, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fffbeb' }}>
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }} ref={editorRef}>{bewerkId ? '✏️ Notitie bewerken' : '✍️ Nieuwe notitie in:'}</Typography>
              {!bewerkId && <Chip size="small" label={selectie.label} sx={{ height: 20, fontSize: 11.5, bgcolor: '#eef2ff', color: '#3730a3', fontWeight: 700 }} />}
              {bewerkId && <Button size="small" onClick={annuleerBewerken} sx={{ textTransform: 'none', fontSize: 12, p: 0, minWidth: 0, color: '#64748b' }}>annuleren</Button>}
              {selectie.hint && <Typography variant="caption" color="text.secondary">{selectie.hint}</Typography>}
              <Box sx={{ flex: 1 }} />
              <Button size="small" startIcon={<ArticleOutlinedIcon sx={{ fontSize: 16 }} />} onClick={(e) => setSjabloonAnchor(e.currentTarget)}
                sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12.5, color: '#475569' }}>Sjabloon</Button>
              <Button size="small" startIcon={<MicNoneIcon sx={{ fontSize: 16 }} />} onClick={() => setMeetingOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12.5, color: '#475569' }}>Meeting koppelen</Button>
            </Stack>
            <Menu anchorEl={sjabloonAnchor} open={!!sjabloonAnchor} onClose={() => setSjabloonAnchor(null)}>
              {SJABLONEN.map((sj) => <MenuItem key={sj.key} onClick={() => kiesSjabloon(sj)} sx={{ fontSize: 13.5 }}>{sj.label}</MenuItem>)}
            </Menu>
            <MeetingKoppelDialog open={meetingOpen} onClose={() => setMeetingOpen(false)}
              projectId={selectie.type === 'project' ? selectie.projectId : ''} onKies={kiesMeeting} />
            <NotitieEditor value={tekst} onChange={setTekst} minHeight={120} autoFocus
              placeholder="Typ hier — iemand belt? Gewoon beginnen. De eerste regel wordt het onderwerp." />
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1.25 }}>
              <Button variant="contained" onClick={bewaar} disabled={busy || !tekst.trim()}
                sx={{ textTransform: 'none', fontWeight: 800 }}>
                {busy ? 'Bezig…' : (bewerkId ? 'Klaar' : 'Opslaan')}
              </Button>
              <Typography variant="caption" sx={{ color: autosaveStatus.includes('mislukt') ? '#b91c1c' : '#94a3b8' }}>
                {autosaveStatus || (tekst.trim().length >= 25 ? 'Wordt tussentijds bewaard tijdens het typen' : '')}
              </Typography>
            </Stack>
          </Box>}

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <Chip size="small" label="Alleen van mij" onClick={() => setAlleenMijn((v) => !v)}
              variant={alleenMijn ? 'filled' : 'outlined'}
              sx={{ fontWeight: 700, fontSize: 11.5, bgcolor: alleenMijn ? '#eef2ff' : undefined, color: alleenMijn ? '#3730a3' : '#64748b', borderColor: '#e2e8f0' }} />
            {alleenMijn && <Typography sx={{ fontSize: 11.5, color: '#94a3b8' }}>notities met jou als auteur</Typography>}
          </Stack>
          {zoekActief && (
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b', mb: 1.5 }}>
              {treffers == null ? 'Zoeken…' : `${treffers.length} treffer${treffers.length === 1 ? '' : 's'} voor “${zoek.trim()}”${treffers.length >= 100 ? ' (eerste 100)' : ''}`}
              {' · Esc wist'}
            </Typography>
          )}
          {(zoekActief ? treffers : notes) == null ? <CircularProgress size={24} /> : (zoekActief ? treffers : notes).length === 0 ? (
            <Typography sx={{ fontFamily: FONT, color: '#94a3b8', textAlign: 'center', py: 4 }}>
              {zoekActief ? 'Niets gevonden.' : 'Nog geen notities hier.'}
            </Typography>
          ) : (zoekActief ? treffers : notes).map((n) => (
            <Box key={n.id} sx={{ mb: 1.5, p: 2, borderRadius: 2, border: '1px solid #f1f5f9', bgcolor: '#fefce8' }}>
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>{n.onderwerp || 'Notitie'}</Typography>
                {n.projectnummer && <Chip size="small" label={`Project ${n.projectnummer}${zoekActief && n.projectnaam ? ` · ${n.projectnaam}` : ''}`} sx={{ height: 18, fontSize: 10.5, bgcolor: '#eef2ff', color: '#3730a3' }} />}
                {zoekActief && n.klant && <Chip size="small" label={n.klant} sx={{ height: 18, fontSize: 10.5, bgcolor: '#f1f5f9', color: '#334155' }} />}
                {n.folder && <Chip size="small" label={`📁 ${n.folder}`} sx={{ height: 18, fontSize: 10.5 }} />}
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  {n.door}{n.at ? ` · ${String(n.at).slice(0, 10)}` : ''}
                </Typography>
                <Tooltip title="Notitie bewerken">
                  <IconButton size="small" onClick={() => bewerk(n)} aria-label="Notitie bewerken"
                    sx={{ p: 0.25, ml: 0.5, color: bewerkId === n.id ? '#3730a3' : '#94a3b8', '&:hover': { color: '#3730a3', bgcolor: '#eef2ff' } }}>
                    <EditOutlinedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Notitie verwijderen">
                  <IconButton size="small" className="kaart-extra" onClick={() => verwijder(n)} aria-label="Notitie verwijderen"
                    sx={{ p: 0.25, ml: 0.5, color: '#cbd5e1', '&:hover': { color: '#b91c1c', bgcolor: '#fee2e2' } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
              <IngeklapteNotitie tekst={n.notes} inklappen={zoekActief} onderwerp={n.onderwerp} />
              {zoekActief && (
                <Button size="small" onClick={() => openVanuitZoek(n)}
                  sx={{ textTransform: 'none', mt: 0.75, p: 0, minWidth: 0, fontSize: 12, fontWeight: 700 }}>
                  Toon in boom →
                </Button>
              )}
              {!zoekActief && selectie.type === 'los' && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                  <VerplaatsNaar boom={boom} onKies={(extra) => verplaats(n, extra)} />
                </Stack>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/** "→ zet bij…": losse notitie alsnog in een account/map/project hangen. */
function VerplaatsNaar({ boom, onKies }) {
  const opties = [];
  (boom || []).forEach((acc) => {
    opties.push({ label: `${acc.account} · Algemeen`, extra: { klantNaam: acc.account } });
    (acc.mappen || []).forEach((m) => opties.push({ label: `${acc.account} · ${m}`, extra: { klantNaam: acc.account, folder: m } }));
    acc.projecten.forEach((p) => opties.push({ label: `${p.nummer}. ${p.naam}`, extra: { projectId: p.id } }));
  });
  const [zoek, setZoek] = useState('');
  const hits = zoek.trim().length >= 2
    ? opties.filter((o) => o.label.toLowerCase().includes(zoek.toLowerCase())).slice(0, 8)
    : [];
  return (
    <Box sx={{ width: '100%' }}>
      <TextField size="small" variant="standard" placeholder="→ zet bij account, map of project…"
        value={zoek} onChange={(e) => setZoek(e.target.value)} sx={{ minWidth: 280 }} />
      {hits.map((h) => (
        <Tooltip key={h.label} title="Klik om te verplaatsen">
          <Chip size="small" label={h.label} onClick={() => { setZoek(''); onKies(h.extra); }}
            sx={{ height: 20, fontSize: 11, m: 0.25, cursor: 'pointer' }} />
        </Tooltip>
      ))}
    </Box>
  );
}
